import { useCallback, useEffect, useRef, useState } from 'react';
import {
  closeAdminTournament,
  createAdminTournament,
  fetchAdminTournaments,
  setAdminTournamentDisplay,
} from '../infrastructure/adminApi';
import { GAME_LOCATIONS } from '../../game/data/gameLocations';
import { renderQrToCanvas } from '../../game/infrastructure/qrCode';
import { buildTournamentUrl } from '../../game/infrastructure/tournamentLinks';
import { TournamentShareModal } from '../../game/presentation/components/TournamentScreens';
import TournamentResultsPanel from './TournamentResultsPanel';

const formatDate = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return '—';
  }
};

function TournamentQr({ url }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    renderQrToCanvas(canvasRef.current, url, { cellSize: 5 });
  }, [url]);
  return (
    <figure className="w-max border-4 border-white bg-white p-1">
      <canvas ref={canvasRef} role="img" aria-label={`Código QR del torneo: ${url}`} />
      <figcaption className="sr-only">{url}</figcaption>
    </figure>
  );
}

const settingsSummary = (tournament) => {
  const settings = tournament?.settings;
  if (!settings) return '—';
  const sceneLabel = GAME_LOCATIONS.find((location) => location.id === settings.sceneLocationId)?.label
    || settings.zone;
  return `Velocidad ${Number(settings.speedMultiplier).toFixed(1)} · ventana ${Number(settings.toleranceLevel).toFixed(1)} · ${sceneLabel}`;
};

export default function TournamentAdminPanel({ authorization, onSessionExpired }) {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [shareTournament, setShareTournament] = useState(null);
  const [selectedTournament, setSelectedTournament] = useState(null);
  const openButtonRef = useRef(null);
  const [form, setForm] = useState({
    name: '',
    speedMultiplier: 1,
    toleranceLevel: 3,
    sceneLocationId: GAME_LOCATIONS[0].id,
  });

  const load = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const payload = await fetchAdminTournaments(authorization);
      setItems(payload.items || []);
    } catch (requestError) {
      if (requestError.status === 401) onSessionExpired?.();
      else setError(requestError.message);
    } finally {
      setIsLoading(false);
    }
  }, [authorization, onSessionExpired]);

  useEffect(() => {
    load();
  }, [load]);

  const active = items.find((item) => item.status === 'active') || null;
  const history = items.filter((item) => item.status !== 'active');

  const openTournament = (tournament, event) => {
    openButtonRef.current = event.currentTarget;
    setSelectedTournament(tournament);
  };

  const closeResults = () => {
    setSelectedTournament(null);
    openButtonRef.current?.focus();
  };

  const handleCreate = async (event) => {
    event.preventDefault();
    setError('');
    setNotice('');
    const scene = GAME_LOCATIONS.find((location) => location.id === form.sceneLocationId) || GAME_LOCATIONS[0];
    setIsSaving(true);
    try {
      const payload = await createAdminTournament(authorization, {
        name: form.name.trim(),
        speedMultiplier: Number(form.speedMultiplier),
        toleranceLevel: Number(form.toleranceLevel),
        zone: scene.gameZone,
        sceneLocationId: scene.id,
      });
      setForm((current) => ({ ...current, name: '' }));
      setNotice(`Torneo “${payload.tournament.name}” activo. Comparte su enlace o QR para que participen.`);
      await load();
    } catch (requestError) {
      if (requestError.status === 401) onSessionExpired?.();
      else setError(requestError.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDisplay = async (tournament, display) => {
    setError('');
    setNotice('');
    try {
      await setAdminTournamentDisplay(authorization, tournament.id, display);
      if (display.visible === true) {
        setNotice(`“${tournament.name}” ya es visible en el mapa y en el ranking.`);
      } else if (display.visible === false) {
        setNotice(`“${tournament.name}” quedó oculto del mapa y del ranking. Su enlace y QR siguen funcionando.`);
      } else if (display.showButton === true) {
        setNotice(`El botón “Torneo” de “${tournament.name}” ya aparece en el mapa.`);
      } else if (display.showButton === false) {
        setNotice(`El botón de “${tournament.name}” quedó oculto: solo se juega con el enlace o QR.`);
      }
      await load();
    } catch (requestError) {
      if (requestError.status === 401) onSessionExpired?.();
      else setError(requestError.message);
    }
  };

  const handleClose = async (tournament) => {
    if (!window.confirm(
      `¿Cerrar “${tournament.name}”?\n\nYa nadie podrá participar. El ranking conservará a la persona ganadora resaltada.`,
    )) return;
    setError('');
    setNotice('');
    setIsSaving(true);
    try {
      const result = await closeAdminTournament(authorization, tournament.id);
      setNotice(result.winner
        ? `Torneo cerrado. Ganó @${result.winner.username} con ${result.winner.score.toLocaleString()} puntos.`
        : 'Torneo cerrado sin participantes.');
      await load();
    } catch (requestError) {
      if (requestError.status === 401) onSessionExpired?.();
      else setError(requestError.message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <section className="mx-auto max-w-[1480px] px-5 py-8 lg:px-8">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[.2em] text-[#9d8cab]">Un torneo activo cada vez</p>
          <h2 className="font-display text-3xl uppercase text-white">Torneos globales</h2>
          <p className="mt-2 max-w-xl text-sm leading-6 text-[#b7a9c4]">
            Todas las personas participantes juegan con la misma velocidad, ventana de golpe, locación y
            mapa musical. Solo se permite <strong className="text-white">una partida por persona</strong>;
            gana el mayor puntaje y el empate lo rompe la precisión.
          </p>
        </div>
        <button
          type="button"
          onClick={() => load()}
          disabled={isLoading}
          className="min-h-[44px] border border-[#594a65] px-4 py-2 text-[10px] font-black uppercase tracking-wider text-[#d6c9df] disabled:opacity-60"
        >
          {isLoading ? 'Cargando…' : 'Actualizar'}
        </button>
      </div>

      {error && <p className="mt-4 border border-[#F7E04B]/40 bg-[#2b1219] p-3 text-sm text-[#ffd8de]" role="alert">{error}</p>}
      {notice && <p className="mt-4 border border-[#7EC54A]/50 bg-[#0F1F12] p-3 text-sm text-[#C5E6A0]" role="status">{notice}</p>}

      {active ? (
        <div className="mt-6 border border-[#F7E04B]/60 bg-[#0d0b11] p-5">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
            <p className="text-[10px] font-black uppercase tracking-[.2em] text-[#F7E04B]">Torneo activo</p>
            {active.visible === false && (
              <span className="border border-[#594a65] px-2 py-0.5 text-[9px] font-black uppercase tracking-wider text-[#a99ab4]">
                Oculto para jugadores
              </span>
            )}
            <label className="ml-auto flex min-h-[44px] cursor-pointer items-center gap-3 text-[10px] font-black uppercase tracking-[.14em] text-[#d6c9df]">
              <span>Sección {active.visible === false ? 'oculta' : 'visible'}</span>
              <input
                type="checkbox"
                role="switch"
                aria-checked={active.visible !== false}
                aria-label="Mostrar la sección del torneo a los jugadores"
                checked={active.visible !== false}
                onChange={(event) => handleDisplay(active, { visible: event.target.checked })}
                className="h-5 w-5 shrink-0 cursor-pointer accent-[#7EC54A]"
              />
            </label>
            <label
              className={[
                'flex min-h-[44px] items-center gap-3 text-[10px] font-black uppercase tracking-[.14em] text-[#d6c9df]',
                active.visible === false ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
              ].join(' ')}
              title={active.visible === false ? 'Activa la sección para usar el botón' : 'Ocultarlo deja el juego solo por enlace o QR'}
            >
              <span>Botón {active.showButton === false ? 'oculto' : 'visible'}</span>
              <input
                type="checkbox"
                role="switch"
                aria-checked={active.showButton !== false}
                aria-label="Mostrar el botón Torneo en el mapa"
                checked={active.showButton !== false}
                disabled={active.visible === false}
                onChange={(event) => handleDisplay(active, { showButton: event.target.checked })}
                className="h-5 w-5 shrink-0 cursor-pointer accent-[#F7E04B] disabled:cursor-not-allowed"
              />
            </label>
          </div>
          <h3 className="mt-1 break-words font-display text-2xl uppercase text-white">{active.name}</h3>
          <p className="mt-2 text-sm text-[#b7a9c4]">{settingsSummary(active)} · {active.entries} participantes</p>
          {active.visible === false && (
            <p className="mt-2 text-xs leading-5 text-[#8f829c]">
              Oculto del mapa general y de la pestaña de ranking. El enlace y el QR de abajo siguen funcionando para participar.
            </p>
          )}
          <div className="mt-4 flex flex-col gap-4 md:flex-row md:items-start">
            <TournamentQr url={buildTournamentUrl(active.inviteToken)} />
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-black uppercase tracking-[.18em] text-[#9d8cab]">Enlace y QR del torneo</p>
              <p className="mt-2 break-all border border-[#7EC54A]/60 bg-black/40 p-3 font-mono text-xs text-[#F7E04B]">
                {buildTournamentUrl(active.inviteToken)}
              </p>
              <div className="mt-3 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => setShareTournament(active)}
                  className="min-h-[44px] border border-[#7EC54A] bg-[#1A4D2E] px-5 text-xs font-black uppercase text-white"
                >
                  Compartir
                </button>
                <a
                  href="/ranking"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex min-h-[44px] items-center border border-[#594a65] px-5 text-xs font-black uppercase text-[#d6c9df]"
                >
                  Ver ranking
                </a>
                <button
                  type="button"
                  onClick={(event) => openTournament(active, event)}
                  aria-expanded={selectedTournament?.id === active.id}
                  aria-controls={selectedTournament ? 'tournament-results' : undefined}
                  className="min-h-[44px] border border-[#d8b6ee] px-5 text-xs font-black uppercase text-[#d6c9df]"
                >
                  Abrir
                </button>
                <button
                  type="button"
                  onClick={() => handleClose(active)}
                  disabled={isSaving}
                  className="min-h-[44px] border border-[#ff2d2d] bg-[#c11212] px-5 text-xs font-black uppercase text-white disabled:opacity-60"
                >
                  {isSaving ? 'Cerrando…' : 'Cerrar torneo'}
                </button>
              </div>
              <p className="mt-3 text-xs leading-5 text-[#8f829c]">
                Al cerrar, nadie más podrá participar y el ranking mostrará a la persona ganadora con su bandera.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <form onSubmit={handleCreate} className="mt-6 border border-[#312a3a] bg-[#0d0b11] p-5">
          <h3 className="font-display text-2xl uppercase text-white">Crear torneo</h3>
          <p className="mt-2 text-sm text-[#b7a9c4]">
            Al crearlo queda activo de inmediato en el mapa general. Solo puede haber uno activo cada vez.
          </p>
          <div className="mt-5 grid gap-4 md:grid-cols-2">
            <label className="text-[10px] font-black uppercase tracking-[.12em] text-[#9d8cab] md:col-span-2">
              Nombre del torneo
              <input
                value={form.name}
                onChange={(event) => setForm((current) => ({ ...current, name: event.target.value.slice(0, 80) }))}
                maxLength={80}
                required
                placeholder="Ej. Copa Festival de Terror"
                className="mt-2 min-h-[44px] w-full border border-[#594a65] bg-black px-3 text-base normal-case tracking-normal text-white outline-none placeholder:text-[#6f6280] focus:border-[#d8b6ee]"
              />
            </label>
            <label className="text-[10px] font-black uppercase tracking-[.12em] text-[#9d8cab]">
              {`Velocidad del ritual (${Number(form.speedMultiplier).toFixed(1)}×)`}
              <input
                type="range"
                min={0.5}
                max={1.5}
                step={0.1}
                value={form.speedMultiplier}
                onChange={(event) => setForm((current) => ({ ...current, speedMultiplier: event.target.value }))}
                className="mt-3 h-2 w-full cursor-pointer appearance-none rounded-lg bg-gray-700 accent-[#F7E04B]"
              />
            </label>
            <label className="text-[10px] font-black uppercase tracking-[.12em] text-[#9d8cab]">
              {`Ventana de golpe (${Number(form.toleranceLevel).toFixed(1)})`}
              <input
                type="range"
                min={1}
                max={6}
                step={0.5}
                value={form.toleranceLevel}
                onChange={(event) => setForm((current) => ({ ...current, toleranceLevel: event.target.value }))}
                className="mt-3 h-2 w-full cursor-pointer appearance-none rounded-lg bg-gray-700 accent-[#7EC54A]"
              />
            </label>
            <label className="text-[10px] font-black uppercase tracking-[.12em] text-[#9d8cab] md:col-span-2">
              Locación (misma para todas las personas participantes)
              <select
                value={form.sceneLocationId}
                onChange={(event) => setForm((current) => ({ ...current, sceneLocationId: event.target.value }))}
                className="mt-2 min-h-[44px] w-full border border-[#594a65] bg-black px-3 text-base normal-case tracking-normal text-white outline-none focus:border-[#d8b6ee]"
              >
                {GAME_LOCATIONS.map((location) => (
                  <option key={location.id} value={location.id}>{location.label} · {location.gameZone}</option>
                ))}
              </select>
            </label>
          </div>
          <p className="mt-3 text-xs leading-5 text-[#8f829c]">
            El mapa musical de hits se genera fijo para el torneo: todas las partidas usan el mismo.
          </p>
          <div className="mt-5">
            <button
              type="submit"
              disabled={isSaving || isLoading}
              className="min-h-[48px] border border-[#F7E04B] bg-[#F7E04B] px-6 py-3 text-[11px] font-black uppercase tracking-[.16em] text-black shadow-[4px_4px_0_rgba(0,0,0,.55)] disabled:opacity-60"
            >
              {isSaving ? 'Creando…' : 'Crear y activar torneo'}
            </button>
          </div>
        </form>
      )}

      {selectedTournament && (
        <TournamentResultsPanel
          key={selectedTournament.id}
          authorization={authorization}
          tournament={items.find((item) => item.id === selectedTournament.id) || selectedTournament}
          onSessionExpired={onSessionExpired}
          onClose={closeResults}
        />
      )}

      <div className="mt-6 overflow-x-auto border border-[#312a3a] bg-[#0d0b11]">
        <table className="w-full min-w-[640px] text-left text-sm">
          <thead>
            <tr className="border-b border-[#312a3a] text-[10px] uppercase tracking-[.16em] text-[#9d8cab]">
              <th className="px-4 py-3">Torneo</th>
              <th className="px-4 py-3">Estado</th>
              <th className="px-4 py-3">Configuración</th>
              <th className="px-4 py-3">Participantes</th>
              <th className="px-4 py-3">Ganador</th>
              <th className="px-4 py-3">Creado</th>
              <th className="px-4 py-3">Detalle</th>
            </tr>
          </thead>
          <tbody>
            {history.map((item) => (
              <tr key={item.id} className="border-b border-[#1d1826] text-[#eee8f5]">
                <td className="px-4 py-3 font-bold">{item.name}</td>
                <td className="px-4 py-3 text-xs font-black uppercase text-[#b7a9c4]">Cerrado</td>
                <td className="px-4 py-3 text-xs text-[#d6c9df]">{settingsSummary(item)}</td>
                <td className="px-4 py-3">{item.entries}</td>
                <td className="px-4 py-3 font-bold text-[#F7E04B]">{item.winner ? `@${item.winner}` : '—'}</td>
                <td className="whitespace-nowrap px-4 py-3 text-xs text-[#b7a9c4]">{formatDate(item.createdAt)}</td>
                <td className="px-4 py-3">
                  <button
                    type="button"
                    onClick={(event) => openTournament(item, event)}
                    aria-label={`Abrir resultados de ${item.name}`}
                    aria-expanded={selectedTournament?.id === item.id}
                    aria-controls={selectedTournament ? 'tournament-results' : undefined}
                    className="min-h-[44px] border border-[#594a65] px-4 py-2 text-xs font-bold text-[#d6c9df]"
                  >
                    Abrir
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!isLoading && history.length === 0 && !error && (
          <p className="px-4 py-10 text-center text-sm text-[#8f829c]">Sin torneos cerrados todavía.</p>
        )}
      </div>

      {shareTournament && shareTournament.inviteToken && (
        <TournamentShareModal
          tournament={shareTournament}
          url={buildTournamentUrl(shareTournament.inviteToken)}
          onClose={() => setShareTournament(null)}
        />
      )}
    </section>
  );
}
